// AgreementView.js

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../../api/axiosinstance";
import { showSuccess, showError } from "../../utils/toastUtils";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import PageTransition from "../../components/PageTransition";
import axiosInstance from "../../api/axiosinstance";

/* ================= RESPONSIVE HOOK ================= */
function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
}

function AgreementView() {
  const { id } = useParams();

  const [html, setHtml] = useState("");

  const navigate = useNavigate();

  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [scrolledBottom, setScrolledBottom] = useState(false);

  const [scrollProgress, setScrollProgress] = useState(0);

  const width = useWindowWidth();
  // 0-640   -> phone (fully stacked, one column)
  // 641-1024 -> tablet (still side-by-side, just narrower)
  // 1025+   -> desktop
  const isMobile = width <= 640;
  const isTablet = width > 640 && width <= 1024;

  /* ================= LOAD AGREEMENT ================= */

  useEffect(() => {
    if (id) {
      trackAgreementView();
    }
  }, [id]);

  const trackAgreementView = async () => {
    try {
      await axiosInstance.post("/client-journey/track", {
        agreementId: id,

        step: "agreement_viewed",

        page: window.location.pathname,

        description: "Client opened agreement page",
      });
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const loadAgreement = async () => {
      try {
        // Production + Local dono ke liye

        const API_BASE_URL =
          process.env.REACT_APP_SOCKET_URL || "http://localhost:5000/";

        const response = await fetch(
          `${API_BASE_URL}api/v1/service-agreement/public/${id}`,
        );

        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const data = await response.text();

        console.log("Agreement HTML Loaded:", data);

        setHtml(data);
      } catch (err) {
        console.log("Agreement Load Error:", err);

        showError("Failed to load agreement");
      }
    };

    if (id) {
      loadAgreement();
    }
  }, [id]);
  /* ================= SCROLL ================= */

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    const totalScrollableHeight = scrollHeight - clientHeight;

    if (totalScrollableHeight <= 0) return;

    const progress = Math.min(
      Math.round((scrollTop / totalScrollableHeight) * 100),
      100,
    );

    // Progress sirf increase hoga
    setScrollProgress((prev) => Math.max(prev, progress));

    // Bottom ek baar hit ho gaya to true hi rahega
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setScrolledBottom(true);
    }
  };

  /* ================= SUBMIT AGREEMENT ================= */

  const handleSubmitAgreement = async () => {
    try {
      setLoading(true);

      const res = await axios.post("/service-agreement/send-otp", {
        agreementId: id,
      });

      await axiosInstance.post("/client-journey/track", {
        agreementId: id,

        step: "agreement_submitted",

        page: window.location.pathname,

        description: "Client submitted agreement",
      });

      if (res?.code === 200) {
        showSuccess("OTP sent successfully");

        navigate(`/agreement/otp/${id}`);
      }
    } catch (err) {
      console.log(err);

      showError(err?.message || "OTP send failed");
    } finally {
      setLoading(false);
    }
  };

  /* ================= VERIFY OTP ================= */

  /* ================= PAYMENT ================= */

  const handlePayment = async (type) => {
    try {
      const res = await axios.post("/payment/create-order", {
        agreementId: id,
        paymentType: type,
      });

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,

        amount: res.data?.amount || res.amount,

        currency: res.data?.currency || res.currency,

        order_id: res.data?.orderId || res.orderId,

        name: "Axepert Services",

        description: type === "full" ? "Full Payment" : "Partial Payment",

        handler: async function (response) {
          try {
            const verifyRes = await axios.post("/payment/verify", {
              razorpay_order_id: response.razorpay_order_id,

              razorpay_payment_id: response.razorpay_payment_id,

              razorpay_signature: response.razorpay_signature,
            });

            if (verifyRes.success) {
              showSuccess("Payment Successful");
            }
          } catch (err) {
            console.log(err);

            showError("Payment verification failed");
          }
        },

        theme: {
          color: "#7b1408",
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.open();
    } catch (err) {
      console.log(err);

      showError("Payment failed");
    }
  };

  return (
    <PageTransition>
      <div
        style={{
          minHeight: "100vh",
          background: "#f4f0ec",
          padding: isMobile ? "16px 10px" : isTablet ? "24px 16px" : "0 20px",
        }}
      >
        {/* MAIN WRAPPER */}

        <div
          style={{
            maxWidth: "1350px",
            margin: "0 auto",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "18px" : isTablet ? "20px" : "35px",
            alignItems: isMobile ? "stretch" : "flex-start",
          }}
        >
          {/* LEFT PANEL */}

          <div
            style={{
              width: isMobile ? "100%" : isTablet ? "230px" : "340px",
              minWidth: isMobile ? "100%" : isTablet ? "230px" : "340px",
              background: "#5a140c",
              borderRadius: isMobile ? "20px" : "28px",
              padding: isMobile
                ? "24px 22px"
                : isTablet
                  ? "28px 22px"
                  : "40px 35px",
              color: "#fff",
              position: isMobile ? "relative" : "sticky",
              top: "20px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                width: isMobile ? "140px" : "220px",
                height: isMobile ? "140px" : "220px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.05)",
                top: "-80px",
                right: "-80px",
              }}
            />

            <img
              src="/assets/img/whitelogo.png"
              alt=""
              style={{
                width: isMobile ? "100px" : isTablet ? "110px" : "130px",
                marginBottom: isMobile ? "20px" : isTablet ? "26px" : "35px",
              }}
            />

            <h1
              style={{
                fontSize: isMobile ? "28px" : isTablet ? "34px" : "42px",
                lineHeight: isMobile ? "36px" : isTablet ? "44px" : "52px",
                fontWeight: "700",
                marginBottom: isMobile ? "16px" : "25px",
                color: "#fff",
              }}
            >
              SERVICE
              <br />
              AGREEMENT
            </h1>

            <div
              style={{
                width: "90px",
                height: "3px",
                background: "#ffffff",
                marginBottom: isMobile ? "18px" : "30px",
              }}
            />

            <p
              style={{
                lineHeight: isMobile ? "24px" : "32px",
                color: "rgba(255,255,255,0.82)",
                fontSize: isMobile ? "13px" : "15px",
              }}
            >
              Please review all terms carefully before proceeding further. This
              agreement becomes effective once you verify the OTP and complete
              payment.
            </p>

            <div
              style={{
                marginTop: isMobile ? "24px" : "45px",
                display: "flex",
                flexDirection: "column",
                gap: isMobile ? "12px" : "18px",
              }}
            >
              <div
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  padding: isMobile ? "12px" : "18px",
                  borderRadius: "18px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "row" : "column",
                    flexWrap: "wrap",
                    gap: isMobile ? "10px" : "18px",
                  }}
                >
                  {/* STEP 1 ACTIVE */}

                  <div
                    style={{
                      flex: isMobile ? "1 1 auto" : "unset",
                      minWidth: isMobile ? "90px" : "auto",
                      background: "rgba(255,255,255,0.22)",

                      border: "1px solid rgba(255,255,255,0.15)",

                      boxShadow: "0 0 20px rgba(255,255,255,0.08)",

                      padding: isMobile ? "10px 12px" : "18px",

                      borderRadius: "14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "11px" : "13px",
                        opacity: 0.7,
                        marginBottom: isMobile ? "4px" : "8px",
                      }}
                    >
                      Step 1
                    </div>

                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: isMobile ? "13px" : "16px",
                      }}
                    >
                      Review Agreement
                    </div>
                  </div>

                  {/* STEP 2 */}

                  <div
                    style={{
                      flex: isMobile ? "1 1 auto" : "unset",
                      minWidth: isMobile ? "90px" : "auto",
                      background: scrolledBottom
                        ? "rgba(255,255,255,0.14)"
                        : "rgba(255,255,255,0.06)",

                      border: "1px solid rgba(255,255,255,0.08)",

                      padding: isMobile ? "10px 12px" : "18px",

                      borderRadius: "14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "11px" : "13px",
                        opacity: 0.7,
                        marginBottom: isMobile ? "4px" : "8px",
                      }}
                    >
                      Step 2
                    </div>

                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: isMobile ? "13px" : "16px",
                      }}
                    >
                      OTP Verification
                    </div>
                  </div>

                  {/* STEP 3 */}

                  <div
                    style={{
                      flex: isMobile ? "1 1 auto" : "unset",
                      minWidth: isMobile ? "90px" : "auto",
                      background: "rgba(255,255,255,0.06)",

                      border: "1px solid rgba(255,255,255,0.08)",

                      padding: isMobile ? "10px 12px" : "18px",

                      borderRadius: "14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "11px" : "13px",
                        opacity: 0.7,
                        marginBottom: isMobile ? "4px" : "8px",
                      }}
                    >
                      Step 3
                    </div>

                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: isMobile ? "13px" : "16px",
                      }}
                    >
                      Payment Process
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT PANEL */}

          <div
            style={{
              flex: 1,
              width: isMobile ? "100%" : "auto",
              minWidth: 0,
            }}
          >
            {/* AGREEMENT CARD */}

            <div
              style={{
                background: "#fff",
                borderRadius: isMobile ? "18px" : "28px",
                overflow: "hidden",
                border: "1px solid #eaded8",
                boxShadow: "0 10px 35px rgba(0,0,0,0.05)",
              }}
            >
              {/* TOP BAR */}

              <div
                style={{
                  padding: isMobile
                    ? "18px 18px"
                    : isTablet
                      ? "22px 24px"
                      : "28px 35px",
                  borderBottom: "1px solid #f0e7e3",
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  gap: isMobile ? "12px" : "0",
                }}
              >
                <div>
                  <h2
                    style={{
                      margin: 0,
                      color: "#5a140c",
                      fontSize: isMobile ? "20px" : "28px",
                      fontWeight: "700",
                    }}
                  >
                    Agreement Details
                  </h2>

                  <div
                    style={{
                      marginTop: "8px",
                      color: "#777",
                      fontSize: isMobile ? "13px" : "14px",
                    }}
                  >
                    Please scroll and review the complete agreement carefully.
                  </div>
                </div>

                <div
                  style={{
                    background: scrolledBottom ? "#e8f7ee" : "#fff5f2",

                    color: scrolledBottom ? "#1b8f45" : "#d35400",

                    padding: isMobile ? "8px 14px" : "12px 18px",

                    borderRadius: "14px",

                    fontSize: isMobile ? "12px" : "14px",
                    fontWeight: "600",
                    whiteSpace: "nowrap",
                  }}
                >
                  {scrolledBottom ? "Agreement Reviewed" : "Scroll Required"}
                </div>
              </div>

              {/* PROGRESS SECTION */}

              <div
                style={{
                  padding: isMobile
                    ? "18px"
                    : isTablet
                      ? "20px 24px"
                      : "25px 35px",
                  background: "#fffdfb",
                  borderBottom: "1px solid #f0e7e3",
                }}
              >
                {/* HEADER */}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "14px",
                    gap: "10px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontWeight: "700",
                        color: "#5a140c",
                        fontSize: isMobile ? "14px" : "16px",
                        marginBottom: "4px",
                      }}
                    >
                      Agreement Read Progress
                    </div>

                    <div
                      style={{
                        color: "#777",
                        fontSize: isMobile ? "12px" : "14px",
                      }}
                    >
                      Please read the complete agreement carefully.
                    </div>
                  </div>

                  <div
                    style={{
                      fontWeight: "700",
                      fontSize: isMobile ? "15px" : "18px",
                      color: scrollProgress >= 100 ? "#16a34a" : "#5a140c",
                    }}
                  >
                    {scrollProgress}%
                  </div>
                </div>

                {/* PROGRESS BAR */}

                <div
                  style={{
                    width: "100%",
                    height: isMobile ? "10px" : "14px",
                    background: "#f1e7e1",
                    borderRadius: "999px",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <motion.div
                    initial={{
                      width: 0,
                    }}
                    animate={{
                      width: `${scrollProgress}%`,
                    }}
                    transition={{
                      duration: 0.4,
                    }}
                    style={{
                      height: "100%",
                      borderRadius: "999px",
                      background:
                        scrollProgress >= 100
                          ? "linear-gradient(to right,#16a34a,#22c55e)"
                          : "linear-gradient(to right,#7b1408,#c96f42)",

                      boxShadow:
                        scrollProgress >= 100
                          ? "0 0 20px rgba(34,197,94,0.4)"
                          : "0 0 20px rgba(201,111,66,0.3)",
                    }}
                  />
                </div>

                {/* COMPLETE MESSAGE */}

                {scrollProgress >= 100 && (
                  <motion.div
                    initial={{
                      opacity: 0,
                      y: 10,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    style={{
                      marginTop: "16px",
                      background: "#ecfdf3",
                      color: "#15803d",
                      border: "1px solid #bbf7d0",
                      borderRadius: "14px",
                      padding: isMobile ? "10px 14px" : "14px 18px",
                      fontWeight: "600",
                      fontSize: isMobile ? "13px" : "15px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: isMobile ? "22px" : "28px",
                        height: isMobile ? "22px" : "28px",
                        borderRadius: "50%",
                        background: "#16a34a",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: isMobile ? "12px" : "14px",
                        fontWeight: "700",
                        flexShrink: 0,
                      }}
                    >
                      ✓
                    </div>
                    You have read 100% agreement successfully.
                  </motion.div>
                )}
              </div>

              {/* SCROLL AREA */}

              <div
                onScroll={handleScroll}
                style={{
                  height: isMobile ? "380px" : isTablet ? "500px" : "620px",
                  overflowY: "auto",
                  padding: isMobile ? "18px" : isTablet ? "24px" : "35px",
                  background: "#fcfbfa",
                  fontSize: isMobile ? "14px" : "16px",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: html,
                  }}
                />
              </div>

              {/* ACTION BAR */}

              {
                <div
                  style={{
                    borderTop: "1px solid #f0e7e3",

                    background: "#fff",

                    padding: isMobile
                      ? "18px"
                      : isTablet
                        ? "22px 24px"
                        : "28px 35px",

                    display: "flex",

                    flexDirection: isMobile ? "column" : "row",

                    justifyContent: "space-between",

                    alignItems: isMobile ? "stretch" : "center",

                    flexWrap: "wrap",

                    gap: isMobile ? "14px" : "20px",
                  }}
                >
                  <div>
                    {!scrolledBottom && (
                      <div
                        style={{
                          color: "#d93025",

                          fontWeight: "600",

                          fontSize: isMobile ? "13px" : "15px",

                          marginBottom: "12px",
                        }}
                      >
                        🔽 Scroll to bottom to enable agreement
                      </div>
                    )}

                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        fontSize: isMobile ? "13px" : "15px",
                        color: "#444",
                      }}
                    >
                      <input
                        type="checkbox"
                        disabled={!scrolledBottom}
                        checked={agreementAccepted}
                        onChange={(e) => {
                          if (scrolledBottom) {
                            setAgreementAccepted(e.target.checked);
                          }
                        }}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      />
                      I have read, understood, and agree to the AxeSell Service
                      Agreement.
                    </label>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        fontSize: isMobile ? "13px" : "15px",
                        color: "#444",
                      }}
                    >
                      <input
                        type="checkbox"
                        disabled={!scrolledBottom}
                        checked={privacyAccepted}
                        onChange={(e) => {
                          if (scrolledBottom) {
                            setPrivacyAccepted(e.target.checked);
                          }
                        }}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      />

                      <span>
                        I agree to the{" "}
                        <a
                          href="https://axesell.com/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#5a140c",
                            fontWeight: "600",
                            textDecoration: "underline",
                          }}
                        >
                          Privacy Policy
                        </a>{" "}
                        and associated policies.
                      </span>
                    </label>
                  </div>

                  <button
                    disabled={!agreementAccepted || !privacyAccepted || loading}
                    onClick={handleSubmitAgreement}
                    style={{
                      width: isMobile ? "100%" : "auto",

                      border: "none",

                      background:
                        agreementAccepted && privacyAccepted && !loading
                          ? "#7b1408"
                          : "#a9a9a9",
                      color: "#fff",

                      padding: isMobile ? "14px 24px" : "16px 38px",

                      borderRadius: "14px",

                      cursor:
                        agreementAccepted && privacyAccepted && !loading
                          ? "pointer"
                          : "not-allowed",
                      fontSize: isMobile ? "15px" : "16px",

                      fontWeight: "600",

                      transition: "0.3s",
                    }}
                  >
                    {loading ? "Processing..." : "Submit Agreement"}
                  </button>
                </div>
              }
            </div>

            {/* OTP SECTION */}

            {/* PAYMENT SECTION */}
          </div>
        </div>
      </div>
    </PageTransition>
  );
}

export default AgreementView;
